// src/modules/profile/components/ProfileEdit/index.js
import React, { useState } from 'react';
import { Card, Form, Button, Image, Row, Col, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Alert from '../../../../shared/components/Alert';
import apiService from '../../../../shared/services/apiService';
import { preprocessImage } from '../../../../shared/utils/imageUtils';
import './styles.css';

const ProfileEdit = ({ profile, onSave, onCancel, alert, showAlert }) => {
    const [formData, setFormData] = useState({
        firstName: profile.user?.firstName || '',
        lastName: profile.user?.lastName || '',
        email: profile.user?.email || '',
        profilePicture: null
    });
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'profilePicture' && files[0]) {
            const file = files[0];
            setFormData(prev => ({
                ...prev,
                profilePicture: file
            }));
            // Create preview URL
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            // Prepare update data
            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email
            };

            // Handle profile picture upload if changed
            if (formData.profilePicture) {
                try {
                    // Step 0: Preprocess image client-side (square crop, max 800x800, JPEG)
                    const processedFile = await preprocessImage(formData.profilePicture, {
                        purpose: 'profile',
                        maxDimension: 800,
                        quality: 0.8
                    });

                    // Step 1: Get presigned URL
                    const presignedResponse = await apiService.post('/attachments/presigned-url', {
                        fileName: processedFile.name,
                        contentType: processedFile.type,
                        temp: false,
                        purpose: 'profile'
                    });

                    if (!presignedResponse.url || !presignedResponse.key) {
                        throw new Error('Failed to get presigned URL');
                    }

                    const { url: presignedUrl, key: s3Key } = presignedResponse;

                    // Step 2: Upload to S3 using presigned URL
                    await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('PUT', presignedUrl, true);
                        xhr.setRequestHeader('Content-Type', processedFile.type);

                        xhr.onload = function() {
                            if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(true);
                            } else {
                                reject(new Error(`Upload failed: ${xhr.status}`));
                            }
                        };

                        xhr.onerror = function() {
                            reject(new Error('Upload failed'));
                        };

                        xhr.send(processedFile);
                    });

                    // Step 3: Store the S3 key (not the full URL to avoid issues with spaces)
                    updateData.profilePicture = s3Key;

                } catch (uploadError) {
                    console.error('Error uploading profile picture:', uploadError);
                    showAlert('error', 'Failed to upload profile picture');
                    setUploading(false);
                    return;
                }
            }

            // Save the profile
            await onSave(updateData);
        } catch (error) {
            console.error('Error updating profile:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Card className="profile-edit-card">
            <Card.Header>
                <h4>
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit Profile
                </h4>
            </Card.Header>
            <Card.Body>
                {alert.show && showAlert && (
                    <Alert 
                        variant={alert.variant}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => showAlert(false)}
                    />
                )}
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={4} className="text-center">
                            <Image
                                src={
                                    previewUrl || profile.user?.profilePicture || '/default-avatar.png'
                                }
                                roundedCircle
                                className="mb-3"
                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                            />
                            <Form.Group>
                                <Form.Label>Change Profile Picture</Form.Label>
                                <Form.Control
                                    type="file"
                                    name="profilePicture"
                                    onChange={handleChange}
                                    accept="image/*"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onCancel} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={uploading}>
                            {uploading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Uploading...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

ProfileEdit.propTypes = {
    profile: PropTypes.shape({
        user: PropTypes.shape({
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            email: PropTypes.string.isRequired,
            profilePicture: PropTypes.string
        }).isRequired
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    alert: PropTypes.shape({
        show: PropTypes.bool.isRequired,
        variant: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired
    }).isRequired,
    showAlert: PropTypes.func.isRequired
};

export default ProfileEdit;
